import { cn } from "@/lib/utils";

/**
 * Delta-X brand mark. Inline SVG so it stays razor-sharp at any size with no
 * asset round-trip. A geometric delta (apex + two legs) with a notched
 * baseline, paired with the DELTA-X wordmark.
 *
 *   compact=true   → just the mark
 *   compact=false  → mark + wordmark (default)
 *
 * `size` = rendered HEIGHT of the mark in px. `variant` picks white (dark
 * theme, default) vs black artwork.
 */
type Variant = "white" | "black";

export function Logo({
  className,
  compact = false,
  size = 28,
  variant = "white",
}: {
  className?: string;
  compact?: boolean;
  size?: number;
  variant?: Variant;
  /** Accepted for backwards-compat with old next/image callers; unused. */
  priority?: boolean;
}) {
  const color = variant === "white" ? "#ffffff" : "#0a0b0d";

  return (
    <span
      className={cn("inline-flex items-center gap-2.5", className)}
      aria-label="Delta-X"
      style={{ color }}
    >
      <svg
        viewBox="0 0 100 100"
        fill="none"
        stroke="currentColor"
        strokeWidth="7.5"
        strokeLinecap="square"
        strokeLinejoin="miter"
        aria-hidden="true"
        style={{ height: size, width: size }}
      >
        {/* Delta: apex with two legs (the Λ) */}
        <path d="M50 13 L12 87 M50 13 L88 87" />
        {/* Baseline, notched short of the right vertex for the dynamic cut */}
        <path d="M12 87 L64 87" />
      </svg>
      {!compact ? (
        <span
          aria-hidden="true"
          style={{
            fontSize: Math.round(size * 0.5),
            fontWeight: 600,
            letterSpacing: "0.16em",
            lineHeight: 1,
            whiteSpace: "nowrap",
          }}
        >
          DELTA-X
        </span>
      ) : null}
    </span>
  );
}
